import axios from "axios";
import { API_DOMAIN, Whatsapp_DOMAIN, wati_DOMAIN } from "./constants";

// Attach the auth token to every request aimed at our own backends.
// Individual call sites no longer need to pass the accessToken header
// themselves (existing ones keep working — this only fills the gap).
const OWN_ORIGINS = [API_DOMAIN, Whatsapp_DOMAIN, wati_DOMAIN].filter(Boolean);

// ── WATI auth broken (revoked refresh token) ──────────────────────────────────
// A routine expired token is already handled proactively server-side (a
// scheduler refreshes it well before it lapses) — a 401 from one of these
// endpoints specifically means the agent's WATI *refresh* token itself has
// been revoked, which requires a human to re-authenticate that agent's WATI
// connection. There's nothing to retry; every further call is guaranteed to
// fail the same way until that happens. This module has no React access, so
// detection (below) and blocking are plain module state; WatiChatContext
// registers the actual UI reaction via setOnWatiAuthError and mirrors its
// own state back into setWatiAuthBlocked.
const WATI_FETCH_URL_PATTERN = /\/wati_messages\/fetch/;

let onWatiAuthError = null;
export const setOnWatiAuthError = (fn) => { onWatiAuthError = fn; };

let watiAuthBlocked = false;
export const setWatiAuthBlocked = (blocked) => { watiAuthBlocked = blocked; };

axios.interceptors.request.use((config) => {
  const url = config.url || "";
  const isOwn = url.startsWith("/") || OWN_ORIGINS.some((origin) => url.startsWith(origin));
  if (isOwn) {
    const token = localStorage.getItem("accessToken");
    if (token && !config.headers?.accessToken) {
      config.headers = config.headers || {};
      config.headers.accessToken = token;
    }
  }
  // Short-circuits every further WATI chat/message fetch before it ever hits
  // the network, once a revoked token has been detected — the network call
  // isn't just wasted, it's the exact same request that keeps re-confirming
  // "still broken," which is precisely the back-to-back spam this exists to
  // stop. Sends aren't blocked here (they already surface their own
  // per-message failure and aren't a spam risk — see WatiChatContext).
  if (watiAuthBlocked && WATI_FETCH_URL_PATTERN.test(url)) {
    return Promise.reject(new axios.Cancel("WATI auth blocked — request skipped"));
  }
  return config;
});

// ── Auto-logout on rejected/expired token ────────────────────────────────────
// The backend replies HTTP 200 with { success: false, message: "not logged in" }
// when token verification fails. AuthContext registers the actual logout via
// setOnUnauthorized (this module has no React access).
let onUnauthorized = null;
export const setOnUnauthorized = (fn) => { onUnauthorized = fn; };

let lastUnauthorizedAt = 0;
// Shared by the axios interceptor below and the Socket.IO connect_error
// handler (SocketContext) — both reject on the same "not logged in" signal.
export const triggerUnauthorized = () => {
  // Parallel calls all fail at once when a session dies — fire logout once
  const now = Date.now();
  if (now - lastUnauthorizedAt > 5000) {
    lastUnauthorizedAt = now;
    onUnauthorized?.();
  }
};

axios.interceptors.response.use(
  (response) => {
    const url = response.config?.url || "";
    const isOwn = url.startsWith("/") || OWN_ORIGINS.some((origin) => url.startsWith(origin));
    if (
      isOwn &&
      !url.includes("/auth/login") &&
      response.data?.success === false &&
      String(response.data?.message || "").trim().toLowerCase() === "not logged in"
    ) {
      triggerUnauthorized();
    }
    return response;
  },
  (error) => {
    // A real HTTP 401 (not our own backend's 200-status "not logged in"
    // convention above) rejects the promise instead of resolving it, so it
    // has to be caught here rather than in the fulfilled handler.
    const url = error.config?.url || "";
    if (error.response?.status === 401 && WATI_FETCH_URL_PATTERN.test(url)) {
      onWatiAuthError?.(error.response?.data?.message || "WATI accessToken issue, Contact Admin");
    }
    return Promise.reject(error);
  }
);
